/**
 * Helpers voor het lezen/schrijven van een heat-map-diagram-configuratie.
 *
 * De config wordt geserialiseerd onder `Diagram.viewState[HEAT_MAP_CONFIG_KEY]`
 * — geen aparte D1-kolom, geen migratie. Zie de doc-block bij
 * `HEAT_MAP_CONFIG_KEY` in @ae/domain/types.ts voor de rationale.
 *
 * De stash-entry hergebruikt `ViewElementState.style` als prop-bag zodat
 * round-tripping door bestaande JSON-paths intact blijft (de renderer
 * negeert deze entry omdat de key niet matcht met een concept/relation-id).
 */
import { HEAT_MAP_CONFIG_KEY, type HeatMapConfig, type ViewState } from "@ae/domain";

export function readHeatMapConfig(viewState: ViewState | undefined | null): HeatMapConfig {
  const entry = viewState?.[HEAT_MAP_CONFIG_KEY];
  const blob = entry?.style as Record<string, unknown> | undefined;
  if (!blob) return {};
  const out: HeatMapConfig = {};
  if (typeof blob.targetTypeId === "string") out.targetTypeId = blob.targetTypeId;
  if (typeof blob.targetStereotypeId === "string") {
    out.targetStereotypeId = blob.targetStereotypeId;
  }
  if (typeof blob.verticalAttribute === "string") out.verticalAttribute = blob.verticalAttribute;
  if (typeof blob.horizontalAttribute === "string") {
    out.horizontalAttribute = blob.horizontalAttribute;
  }
  return out;
}

/**
 * Bouwt een nieuwe viewState met de config bijgewerkt. Mutates niet.
 */
export function writeHeatMapConfig(viewState: ViewState, config: HeatMapConfig): ViewState {
  const blob: Record<string, unknown> = {};
  if (config.targetTypeId) blob.targetTypeId = config.targetTypeId;
  if (config.targetStereotypeId) blob.targetStereotypeId = config.targetStereotypeId;
  if (config.verticalAttribute) blob.verticalAttribute = config.verticalAttribute;
  if (config.horizontalAttribute) blob.horizontalAttribute = config.horizontalAttribute;
  return {
    ...viewState,
    // Sentinel-entry: type+ref vereist sinds Fase 2; we hergebruiken de
    // key zelf als ref-value (geen concept/relation-target).
    [HEAT_MAP_CONFIG_KEY]: { type: "object", ref: HEAT_MAP_CONFIG_KEY, style: blob },
  };
}

export function isHeatMapConfigured(config: HeatMapConfig): boolean {
  return Boolean(
    (config.targetTypeId || config.targetStereotypeId) &&
      config.verticalAttribute &&
      config.horizontalAttribute,
  );
}
